import { useEffect, useState } from "react";
import type { BusinessListingApplicationRequest, BusinessListingMarketplaceItemResponse } from "../../types/businessListingTypes";
import { mediaApi } from "../../lib/media";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { formatBusinessListingCompensation, formatBusinessListingDuration } from "../../utils/BusinessListingUtils";
import { formatCityStateZip } from "../../utils/LocationUtils";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

type BusinessListingApplyModalProps = {
  isOpen: boolean;
  listing: BusinessListingMarketplaceItemResponse | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: BusinessListingApplicationRequest) => Promise<void>;
};

const BusinessListingApplyModal = ({
  isOpen,
  listing,
  isSubmitting,
  onClose,
  onSubmit,
}: BusinessListingApplyModalProps) => {
  const [message, setMessage] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMessage("");
      setResumeFile(null);
      setResumeError(null);
      setIsUploadingResume(false);
    }
  }, [isOpen]);

  const isBusy = isSubmitting || isUploadingResume;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setResumeError(null);
      let resumeMediaFileId: string | undefined;
      if (resumeFile) {
        setIsUploadingResume(true);
        const uploadedResume = await mediaApi.uploadFile(resumeFile, "BUSINESS_LISTING_RESUME");
        resumeMediaFileId = uploadedResume.id;
      }
      await onSubmit({
        message: message.trim().length > 0 ? message.trim() : undefined,
        resumeMediaFileId,
      });
    } catch (error) {
      if (resumeFile) {
        setResumeError((current) => current ?? apiErrorToMessage(error, "Unable to upload the resume."));
      }
    } finally {
      setIsUploadingResume(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Apply for Job">
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        {listing ? (
          <div className="space-y-2 rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-sm text-myteengig-text-primary">
            <p className="font-semibold">{listing.businessName || "Business"} • {listing.title}</p>
            <p>{formatCityStateZip(listing)}</p>
            <p>{formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)}</p>
            {listing.estimatedDurationMinutes ? (
              <p>Usual shift: {formatBusinessListingDuration(listing.estimatedDurationMinutes)}</p>
            ) : null}
          </div>
        ) : null}

        <div>
          <label htmlFor="business-listing-application-message" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Message (optional)
          </label>
          <textarea
            id="business-listing-application-message"
            maxLength={5000}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            rows={5}
            placeholder="Share why you're interested, relevant experience, or when you're usually available."
          />
          <p className="mt-2 text-xs text-myteengig-text-muted">
            When you apply, the business may also see your profile photo, age, profile description, and skills from your teen profile.
          </p>
        </div>

        <div>
          <label htmlFor="business-listing-application-resume" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Resume (optional)
          </label>
          <input
            id="business-listing-application-resume"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(event) => {
              const nextFile = event.target.files?.[0] ?? null;
              setResumeError(null);
              if (!nextFile) {
                setResumeFile(null);
                return;
              }
              const allowedTypes = new Set([
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              ]);
              if (!allowedTypes.has(nextFile.type)) {
                setResumeFile(null);
                setResumeError("Resume must be a PDF, DOC, or DOCX file.");
                return;
              }
              if (nextFile.size > 8 * 1024 * 1024) {
                setResumeFile(null);
                setResumeError("Resume exceeds the 8 MB size limit.");
                return;
              }
              setResumeFile(nextFile);
            }}
            className="sr-only"
            disabled={isBusy}
          />
          <label
            htmlFor="business-listing-application-resume"
            className={`block rounded-2xl border px-4 py-4 transition ${
              isBusy
                ? "cursor-not-allowed border-myteengig-border bg-myteengig-background/60 opacity-70"
                : "cursor-pointer border-myteengig-primary/35 bg-myteengig-background hover:border-myteengig-primary hover:bg-white"
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-myteengig-text-primary">
                  {resumeFile ? "Choose a different resume" : "Click here to choose a resume file"}
                </p>
                <p className="mt-1 text-sm text-myteengig-text-muted">
                  {resumeFile ? resumeFile.name : "PDF, DOC, or DOCX"}
                </p>
              </div>
              <span className="rounded-full border border-myteengig-primary/30 bg-white px-3 py-1 text-xs font-semibold text-myteengig-text-primary">
                {resumeFile ? "Replace File" : "Choose File"}
              </span>
            </div>
          </label>
          <p className="mt-2 text-xs text-myteengig-text-muted">Accepted: PDF, DOC, or DOCX up to 8 MB.</p>
          {resumeFile ? (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-myteengig-border bg-myteengig-background px-3 py-2 text-sm text-myteengig-text-primary">
              <span className="min-w-0 truncate">{resumeFile.name}</span>
              <button
                type="button"
                className="text-sm font-medium text-myteengig-text-muted hover:text-myteengig-text-primary"
                onClick={() => {
                  setResumeFile(null);
                  setResumeError(null);
                }}
                disabled={isBusy}
              >
                Remove
              </button>
            </div>
          ) : null}
          {resumeError ? <p className="mt-2 text-sm text-red-600">{resumeError}</p> : null}
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="submit" disabled={isBusy}>
            {isUploadingResume ? "Uploading Resume..." : isSubmitting ? "Applying..." : "Apply for Job"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BusinessListingApplyModal;
